import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Separator } from '../ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Users, Plus, Copy, UserPlus, Clock, Settings as SettingsIcon } from 'lucide-react';
import { InviteRequestsList } from './InviteRequestsList';
import { ReferralChainList } from './ReferralChainList';
import { ShareInviteCard } from './ShareInviteCard';
import { InviteRedemptionsList } from './InviteRedemptionsList';
import { useAuth } from '../../context/AuthContext';
import { formatTtlLabel } from '../ConversationTTLPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewChatDialog = ({ isOpen, onClose, contacts, onStartChat, onCreateGroup, onOpenSettings }) => {
  const { user } = useAuth();
  const [tab, setTab] = useState('chat');
  const [inviteCode, setInviteCode] = useState('');
  const [myInvites, setMyInvites] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Show the user their current default TTL so the choice is visible
  // at conversation-creation time rather than buried in Settings.
  const defaultTtl = Number(user?.default_ttl_hours ?? 24);
  const ttlLabel = formatTtlLabel(defaultTtl);

  useEffect(() => {
    if (isOpen) {
      fetchInvites();
    }
  }, [isOpen]);

  const fetchInvites = async () => {
    try {
      const res = await axios.get(`${API}/invites`);
      setMyInvites(res.data);
    } catch (e) {}
  };

  const createInvite = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/invites`, { max_uses: 1, expires_in_hours: 48 });
      // Immediately update the list with the new invite
      setMyInvites((prev) => {
        // Check if invite already exists to avoid duplicates
        const exists = prev.find((inv) => inv.id === res.data.id);
        if (exists) return prev;
        return [res.data, ...prev];
      });
      toast.success('Invite code created!');
    } catch (error) {
      toast.error('Failed to create invite');
    } finally {
      setLoading(false);
    }
  };

  const useInvite = async () => {
    if (!inviteCode.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/invites/use`, { code: inviteCode });
      toast.success(`Added ${res.data.contact.username} as a contact!`);
      setInviteCode('');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid invite code');
    } finally {
      setLoading(false);
    }
  };

  const copyInvite = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Invite code copied!');
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedContacts.length === 0) {
      toast.error('Please enter a group name and select contacts');
      return;
    }
    setLoading(true);
    try {
      await onCreateGroup(groupName, selectedContacts);
      setGroupName('');
      setSelectedContacts([]);
      onClose();
    } catch (error) {
      toast.error('Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const toggleContact = (contactId) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Conversation</DialogTitle>
          <DialogDescription>Start a chat, create a group, or manage invites</DialogDescription>
        </DialogHeader>

        {/* Inline default-TTL hint — surfaces the auto-destruct choice
            at the moment a new conversation is being created so the
            user doesn't have to remember Settings → Privacy. The chip
            also acts as a one-click shortcut into the setting. */}
        <button
          type="button"
          onClick={() => {
            if (onOpenSettings) onOpenSettings();
          }}
          className="mt-3 group flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-emerald-400/15 bg-emerald-400/[0.04] hover:bg-emerald-400/[0.08] hover:border-emerald-400/30 transition-colors text-left w-full"
          data-testid="new-chat-default-ttl-hint"
        >
          <div className="flex items-center gap-2 text-xs">
            <Clock className="w-3.5 h-3.5 text-emerald-400" strokeWidth={2} />
            <span className="text-slate-300">
              New conversations start at{' '}
              <span
                className="text-emerald-300 font-semibold"
                data-testid="new-chat-default-ttl-value"
              >
                {ttlLabel}
              </span>
            </span>
          </div>
          <span className="text-[10px] uppercase tracking-[1.5px] text-slate-500 group-hover:text-emerald-400 transition-colors flex items-center gap-1">
            <SettingsIcon className="w-3 h-3" />
            change
          </span>
        </button>

        <Tabs value={tab} onValueChange={setTab} className="mt-4">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="chat" data-testid="tab-chat">Chat</TabsTrigger>
            <TabsTrigger value="group" data-testid="tab-group">Group</TabsTrigger>
            <TabsTrigger value="invites" data-testid="tab-invites">Invites</TabsTrigger>
          </TabsList>

          <TabsContent value="chat" className="mt-4 space-y-3">
            {contacts.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No contacts yet</p>
                <p className="text-sm">Use the Invites tab to add contacts</p>
              </div>
            ) : (
              <ScrollArea className="h-64">
                {contacts.map((contact) => (
                  <button
                    key={contact.id}
                    onClick={() => {
                      onStartChat(contact);
                      onClose();
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors"
                    data-testid={`new-chat-contact-${contact.id}`}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-secondary">
                        {contact.username[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-left">
                      <p className="text-sm font-medium text-white">{contact.username}</p>
                      <p className="text-xs text-slate-500">
                        {contact.is_online ? 'Online' : 'Offline'}
                      </p>
                    </div>
                  </button>
                ))}
              </ScrollArea>
            )}
          </TabsContent>

          <TabsContent value="group" className="mt-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Group Name</label>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Enter group name"
                data-testid="group-name-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Select Members</label>
              <ScrollArea className="h-40 border border-border rounded-lg p-2">
                {contacts.map((contact) => (
                  <button
                    key={contact.id}
                    onClick={() => toggleContact(contact.id)}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                      selectedContacts.includes(contact.id)
                        ? 'bg-primary/20 border border-primary/30'
                        : 'hover:bg-secondary/50'
                    }`}
                    data-testid={`group-contact-${contact.id}`}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-secondary text-xs">
                        {contact.username[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-white">{contact.username}</span>
                  </button>
                ))}
              </ScrollArea>
            </div>
            <Button
              onClick={handleCreateGroup}
              disabled={loading || !groupName.trim() || selectedContacts.length === 0}
              className="w-full"
              data-testid="create-group-btn"
            >
              Create Group
            </Button>
          </TabsContent>

          <TabsContent value="invites" className="mt-4 space-y-4">
            {/* One-tap share surface — auto-mints a fresh invite on open
                if the user has none, so the flow is never dead-ended. */}
            <ShareInviteCard
              invites={myInvites}
              onCreateInvite={createInvite}
              creating={loading}
            />

            {/* Live "who accepted your invites" surface — polls every
                20s while the Invites tab is active so a fresh redemption
                shows up without the user reopening the dialog. */}
            <InviteRedemptionsList
              isActive={isOpen && tab === 'invites'}
              onStartChat={async (contact) => {
                await onStartChat(contact);
                onClose();
              }}
            />

            <Separator />

            <div className="space-y-2">
              <label className="text-sm text-slate-300">Use Invite Code</label>
              <div className="flex gap-2">
                <Input
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="font-mono"
                  data-testid="use-invite-input"
                />
                <Button onClick={useInvite} disabled={loading} data-testid="use-invite-btn">
                  <UserPlus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm text-slate-300">Your Invites</label>
                <Button variant="ghost" size="sm" onClick={createInvite} disabled={loading} data-testid="create-invite-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Create
                </Button>
              </div>
              <ScrollArea className="h-32">
                {myInvites.length === 0 ? (
                  <p className="text-center text-sm text-slate-500 py-4">No invites created</p>
                ) : (
                  <div className="space-y-2">
                    {myInvites.map((invite) => (
                      <div
                        key={invite.id}
                        className="flex items-center justify-between p-2 rounded-lg bg-secondary/30"
                      >
                        <div>
                          <p className="font-mono text-sm text-white">{invite.code}</p>
                          <p className="text-xs text-slate-500">
                            {invite.uses}/{invite.max_uses} uses
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => copyInvite(invite.code)}
                          data-testid={`copy-invite-${invite.code}`}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            <Separator />

            <InviteRequestsList />

            <Separator />

            <ReferralChainList />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
